<script lang="ts" setup>
import { PropType } from 'vue';
import { SpamNumberModel, Permission } from '@/types';
import { applyPhoneMask } from '@/utils/number';
import { PencilAltIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/vue/outline';

const { modelValue } = defineProps({
    modelValue: {
        type: Object as PropType<SpamNumberModel>,
        required: true,
    },
});

const emit = defineEmits(['activate', 'inactivate']);
const handleActivateClick = () => emit('activate', modelValue);
const handleInactivateClick = () => emit('inactivate', modelValue);
</script>

<template>
    <UiPanel class="space-y-2 hover:bg-white">
        <div class="flex items-center justify-between">
            <div class="flex-1">
                <span class="flex items-center space-x-2 font-medium text-gray-800">
                    <component
                        :is="$can(Permission.VIEW_SPAM_NUMBER) ? 'RouterLink' : 'span'"
                        :to="{
                            name: 'ViewSpamNumberById',
                            params: {
                                id: modelValue.id,
                            },
                        }"
                        class="text-md text-primary truncate"
                    >
                        {{ applyPhoneMask(modelValue.number) }}
                    </component>
                    <UiIsActiveBadge :model-value="modelValue" class="text-xs" />
                </span>
            </div>
            <div v-if="$can(Permission.EDIT_SPAM_NUMBER)" class="flex space-x-1">
                <UiActionButton
                    v-if="modelValue.active"
                    v-tippy="$t('core.actions.Inactivate')"
                    variant="danger"
                    @click="handleInactivateClick"
                >
                    <span class="sr-only"> {{ $t('core.actions.Inactivate') }} </span>
                    <XCircleIcon class="h-6 w-6" aria-hidden="true" />
                </UiActionButton>
                <UiActionButton
                    v-else
                    v-tippy="$t('core.actions.Activate')"
                    variant="success"
                    @click="handleActivateClick"
                >
                    <span class="sr-only"> {{ $t('core.actions.Activate') }} </span>
                    <CheckCircleIcon class="h-6 w-6" aria-hidden="true" />
                </UiActionButton>
                <UiActionButton
                    v-tippy="$t('core.actions.Edit')"
                    variant="primary"
                    :to="{
                        name: 'EditSpamNumberById',
                        params: {
                            id: modelValue.id,
                        },
                    }"
                >
                    <span class="sr-only"> {{ $t('core.actions.Edit') }} </span>
                    <PencilAltIcon class="h-6 w-6" aria-hidden="true" />
                </UiActionButton>
            </div>
        </div>
        <UiAuditInfo :model-value="modelValue" class="justify-end" />
    </UiPanel>
</template>
